import { FC, useEffect, useState } from "react";
import { Accordion } from "../../../components/controls/accordion"
import { Checkbox } from 'antd';
import { v4 as uuidv4} from 'uuid';
import { DatePeriodPanel } from "../../../hoc/DatePeriodPanel/DatePeriodPanel";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";
import { DefaultDatePeriod } from "../../../system/hooks/useGridFilter";
import FlexColumn from "../../../components/controls/FlexColumn";
import { NumberInput } from "../../../components/controls/inputs/BaseInput";
import styles from "../AnalysisLot/Styles/AnalysisFilterLot.module.scss"
import { useTranslation } from "react-i18next";
import { IOption, Select } from "../../../components/selects/select";
import FlexRow from "../../../components/controls/FlexRow";
import { DateTime } from "luxon";
import { useLocalStorageChange } from "../../../system/hooks/useLocalStorageChange";
import { IDataFilter, IJournalFilters, typeReport } from "../DashboardBusinessControls/interfaces";

interface Props {
    onOpenAccordion: (name: TypeAccordionName) => void
    setFilters:(filters: IAnalysisFilters) => void
    onFiltersLoaded: () => void;
    currentAccordion?: TypeAccordionName;
    tabId?: string;    
}

export type TypeAccordionName = 'expiringDate' | 'lliquidLots' | 'salesSpeed' | 'none'

interface IlliquidLots {
    datePriod: IDatePeriod,
    sales: number,
    percentRemain: number,
    lifeLot: number
}

interface ISalesSpeed {
    datePriod: IDatePeriod,
    isExcessStock: boolean
}

interface IExpiringDate {
    bestBeforeMonth: number,
    compareBestBeforeMonth: IOption
    percentBestBefore: number
    comparePercentBestBefore: IOption
    datePriod: IDatePeriod,
}

export interface IAnalysisFilters {
    lliquidLots: IlliquidLots
    salesSpeed: ISalesSpeed
    expiringDate: IExpiringDate,
    IsGnvls?: boolean
}

export const defaultAnalysisFilters = {
    expiringDate: {
        bestBeforeMonth: 0,
        percentBestBefore: 0,
        compareBestBeforeMonth: {displayName: '<=', value: 'LessOrEq' as OperatorType}, // изменен на меньше либо равно в рамках задачи 93313
        comparePercentBestBefore: {displayName: '>=', value: 'MoreOrEq' as OperatorType},
    },
    lliquidLots: {
        datePriod: DefaultDatePeriod,
        lifeLot: 0,
        sales: 0,
        percentRemain: 0
    },
    salesSpeed: {
        datePriod: DefaultDatePeriod,
        isExcessStock: false
    }
} as IAnalysisFilters

export const AnalyticalFilters: FC<Props> = ({
    onOpenAccordion,
    setFilters,
    currentAccordion,
    onFiltersLoaded,
    tabId
}) => {

    const [activeAccordion, setActiveAccordion] = useState<TypeAccordionName>('none');
    const [savedFilters, setSavedFilters] = useLocalStorageChange<IJournalFilters>('journalFilters');  
  
    const getDatePeriodForFilter = (type: typeReport, dataFilter: IDataFilter): IDatePeriod => {    
        //Закоментировал выгрузку даты с sysOption потому что в этом случае дата останется статичной, а клиент оказыается требует динамической
        switch(type) {
            case typeReport.Illiquid:
                return {
                    startDate:/* (dataFilter?.LliquidLots?.StartDate && DateTime.fromISO(dataFilter?.LliquidLots?.StartDate.toString())) ?? */DateTime.local(1900, 1, 1),
                    endDate: /*  (dataFilter?.LliquidLots?.EndDate && DateTime.fromISO(dataFilter?.LliquidLots?.EndDate.toString())) ?? */DateTime.now().plus({ days: -180 }),
                };
            case typeReport.ExcessStock:
                return {
                    startDate: /* (dataFilter?.ExcessStock?.StartDate && DateTime.fromISO(dataFilter?.ExcessStock?.StartDate.toString())) ?? */DateTime.local(1900, 1, 1),
                    endDate: /*(dataFilter?.ExcessStock?.EndDate && DateTime.fromISO(dataFilter?.ExcessStock?.EndDate.toString())) ?? */ DateTime.now(),
                };
            case typeReport.Expired:
                return {
                    startDate: /* (dataFilter?.Expired?.StartDate && DateTime.fromISO(dataFilter?.Expired?.StartDate.toString())) ?? */ DateTime.now().plus({ days: -90 }),
                    endDate: /* (dataFilter?.Expired?.EndDate && DateTime.fromISO(dataFilter?.Expired?.EndDate.toString())) ??  */ DateTime.now(),
                };
            default:
                return DefaultDatePeriod;
        }
    };

    useEffect(() => {
        if (!savedFilters || !tabId) return;        
        
        if (savedFilters.tabId === tabId) {
            processFilters(savedFilters);           
            setSavedFilters(null);
        }
    }, [savedFilters, tabId]);    
    
    const processFilters = (filters: IJournalFilters) => {
        try {
            const updatedFilters = { ...defaultAnalysisFilters };
            let accordionToOpen: TypeAccordionName = 'none';

            updatedFilters.IsGnvls = filters.filters.IsGnvls
            
            if (filters.reportType === typeReport.Illiquid) {
                updatedFilters.lliquidLots = {
                    ...updatedFilters.lliquidLots,
                    lifeLot: Number(filters.filters?.LliquidLots?.LifeLot) || 50,
                    sales: Number(filters.filters?.LliquidLots?.Sales) || 0,
                    datePriod: getDatePeriodForFilter(typeReport.Illiquid, filters.filters)
                };
                accordionToOpen = 'lliquidLots';
                setActiveAccordion('lliquidLots')
            }
            else if (filters.reportType === typeReport.Expired) {
                updatedFilters.expiringDate = {
                    ...updatedFilters.expiringDate,
                    bestBeforeMonth: Number(filters.filters?.Expired?.BestBeforeMonth) || 2, // данный фильтр добавлен в рамках задачи 93113, не передается с бэка
                    compareBestBeforeMonth: {
                        displayName:filters.filters?.Expired?.CompareBestBeforeMonth?.DisplayName ?? "<=",
                        value : filters.filters?.Expired?.CompareBestBeforeMonth?.Value ?? "LessOrEq"
                    },
                    datePriod: getDatePeriodForFilter(typeReport.Expired, filters.filters)
                };
                accordionToOpen = 'expiringDate';
                setActiveAccordion('expiringDate')
            }
            else if (filters.reportType === typeReport.ExcessStock) {
                updatedFilters.salesSpeed = {
                    ...updatedFilters.salesSpeed,
                    isExcessStock: Boolean(filters.filters.ExcessStock?.IsExcessStock),
                    datePriod: getDatePeriodForFilter(typeReport.ExcessStock, filters.filters)
                };
                accordionToOpen = 'salesSpeed';
                setActiveAccordion('salesSpeed')
            }
            
            setAnalysisFilters(updatedFilters);
            setFilters(updatedFilters);
            onOpenAccordion(accordionToOpen);            
            setSavedFilters(null);
            localStorage.removeItem('journalFilters');            
            onFiltersLoaded();


        } catch (error) {
            console.error('Error processing filters:', error);
        }
    };

    const defaultSearchOptions = [
        {displayName: '=', value: 'Eq' as OperatorType},
        {displayName: '>', value: 'More' as OperatorType},
        {displayName: '>=', value: 'MoreOrEq' as OperatorType},
        {displayName: '<', value: 'Less' as OperatorType},
        {displayName: '<=', value: 'LessOrEq' as OperatorType},
    ] as IOption[];

    const [analysisFilters, setAnalysisFilters] = useState<IAnalysisFilters>(defaultAnalysisFilters)
    const { t } = useTranslation();
    const baseT = (value: string) => t('analysisLot.' + value)

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(analysisFilters);
        }, 200);
        return () => clearTimeout(timer);
    }, [analysisFilters]);

    const handleAccordionToggle = (name: TypeAccordionName, isOpen: boolean) => {
        setActiveAccordion(isOpen ? name : 'none');
        onOpenAccordion(isOpen ? name : 'none');
    };

    const handleExcessStockChange = (e: { target: { checked: boolean } }) => {
        const checked = e.target.checked;
        
        const newFilters = {
            ...analysisFilters,
            salesSpeed: {
                ...analysisFilters.salesSpeed,
                isExcessStock: checked
            }
        };
        
        setAnalysisFilters(newFilters);
        setFilters(newFilters);      
       
    };

    return <>
        <h1>{baseT('analysisFilter')}</h1>
        <Accordion         
            opened={activeAccordion === 'lliquidLots'}
            caption={baseT('isIlliquidLots')} 
            id={`${uuidv4()}`}
            onOpen={(isOpen) => handleAccordionToggle('lliquidLots', isOpen)}
        >
             <FlexColumn className={styles.flexColumn}>
                <DatePeriodPanel
                    datePeriod={analysisFilters.lliquidLots.datePriod}
                    label={baseT("lliquidLotsDatePriod")}
                    onDatePeriodChange={(date: IDatePeriod) => {
                        setAnalysisFilters({...analysisFilters, lliquidLots: {...analysisFilters.lliquidLots, datePriod: date} })
                    }}
                />

                <NumberInput className={styles.numberInput} label={baseT("lliquidLotsSales")} value={analysisFilters.lliquidLots.sales} 
                    onChange={(value) => setAnalysisFilters({...analysisFilters, lliquidLots: {...analysisFilters.lliquidLots, sales: value} })} 
                />
                    
                <NumberInput className={styles.numberInput}label={baseT("lliquidLotsPercentRemain")} value={analysisFilters.lliquidLots.percentRemain} 
                    onChange={(value) => setAnalysisFilters({...analysisFilters, lliquidLots: {...analysisFilters.lliquidLots, percentRemain: value} })}   
                    max={100}
                    min={0}
                />
                <NumberInput className={styles.numberInput} label={baseT("lliquidLotsLifeLot")} value={analysisFilters.lliquidLots.lifeLot} 
                    onChange={(value) => setAnalysisFilters({...analysisFilters, lliquidLots: {...analysisFilters.lliquidLots, lifeLot: value} })}
                />
                    
            </FlexColumn>
        </Accordion>

        <Accordion 
            opened={activeAccordion === 'salesSpeed'}
            caption={baseT('salesSpeed')} 
            id={`${uuidv4()}`}
            onOpen={(isOpen) => handleAccordionToggle('salesSpeed', isOpen)}
        
        >
         <FlexColumn className={styles.flexColumn}>
            <DatePeriodPanel
                    datePeriod={analysisFilters.salesSpeed.datePriod}
                    label={baseT('lliquidLotsDatePriod')}
                    onDatePeriodChange={(date: IDatePeriod) => {
                        setAnalysisFilters({...analysisFilters, salesSpeed: {...analysisFilters.salesSpeed, datePriod: date} })
                    }}
            />
            <div>
                <Checkbox
                    name="isExcessStock"
                    className={styles.customCheckbox}
                    checked={analysisFilters.salesSpeed.isExcessStock}
                    onChange={handleExcessStockChange}                    
                >
                    {baseT('isExcessStock')}
                </Checkbox>
            </div>
         </FlexColumn>
        </Accordion>

        <Accordion 
            opened={activeAccordion === 'expiringDate'}
            caption={baseT('isExpiringDate')} 
            id={`${uuidv4()}`}
            onOpen={(isOpen) => handleAccordionToggle('expiringDate', isOpen)}        
        >
            <FlexColumn className={styles.flexColumn}>
                <FlexRow>
                    <NumberInput className={styles.numberInput} label={baseT('expiringDateBestBeforeMonth')} value={analysisFilters.expiringDate.bestBeforeMonth} 
                        onChange={(value) => setAnalysisFilters({...analysisFilters, expiringDate: {...analysisFilters.expiringDate, bestBeforeMonth: value} })}                    
                    />
                    <Select 
                        label={baseT('compare')} 
                        defaultOption={defaultSearchOptions[2]}
                        options={defaultSearchOptions}
                        value={analysisFilters.expiringDate.compareBestBeforeMonth}
                        onSelect={(option) => setAnalysisFilters({...analysisFilters, expiringDate: {...analysisFilters.expiringDate, compareBestBeforeMonth : option} })}
                    />
                </FlexRow>

                <FlexRow>
                    <NumberInput className={styles.numberInput}label={baseT('expiringDatePercentBestBefore')} value={analysisFilters.expiringDate.percentBestBefore} 
                        onChange={(value) => setAnalysisFilters({...analysisFilters, expiringDate: {...analysisFilters.expiringDate, percentBestBefore: value} })}
                        max={100}
                        min={0}
                    />
                    <Select 
                        label={baseT('compare')}
                        defaultOption={defaultSearchOptions[2]}
                        value={analysisFilters.expiringDate.comparePercentBestBefore}
                        options={defaultSearchOptions}
                        onSelect={(option) => setAnalysisFilters({...analysisFilters, expiringDate: {...analysisFilters.expiringDate, comparePercentBestBefore : option} })}
                    />
                </FlexRow>
                        
                
            </FlexColumn>
        
         
        </Accordion>
    </>
    
}